import { ehLojaDigital } from '@/lib/loja-digital';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Grid3X3,
  List,
  Clock,
  HelpCircle,
  Plus,
  ArrowRight,
  Package,
  ShoppingBag,
  Server,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { useCategories } from '@/hooks/useCategories';
import { useProducts } from '@/hooks/useProducts';
import { useServers } from '@/hooks/useServers';
import { useAvatar } from '@/hooks/useAvatar';
import PageHero from '@/components/common/PageHero';
import CategoryCard from '@/components/store/CategoryCard';
import ProductCard from '@/components/store/ProductCard';
import { CartPreview } from '@/components/store/CartPreview';
import { AddCreditsDialog } from '@/components/credits/AddCreditsDialog';
import { comAnuncioNoCatalogo } from '@/templates/comum/anuncios/noCatalogo';

export default function StorePage() {
  const { t } = useTranslation();
  const { serverId, categorySlug } = useParams<{ serverId?: string; categorySlug?: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const { config } = useTheme();
  const { store } = useStore();
  const lojaDigital = ehLojaDigital(store);
  const [viewMode, setViewMode] = useState('grid');
  const [creditsDialogOpen, setCreditsDialogOpen] = useState(false);
  const { getAvatarUrl } = useAvatar();

  // Data fetching
  const { servers, loading: loadingServers } = useServers(store?.id);
  const { categories, loading: loadingCategories } = useCategories(store?.id, undefined, serverId);
  const { products: allProducts, loading: loadingProducts } = useProducts(store?.id, undefined, serverId);

  useEffect(() => {
    if (isAuthenticated) refreshUser();
  }, [isAuthenticated, refreshUser]);

  // Resolve selected server and category
  const selectedServer = serverId ? servers.find((s) => s.id === serverId) : null;
  const selectedCategory = categorySlug
    ? categories.find((c) => c.slug === categorySlug)
    : null;

  // Redirect if server or category not found after loading
  useEffect(() => {
    if (serverId && !loadingServers && servers.length > 0 && !selectedServer) {
      navigate('/loja', { replace: true });
    }
  }, [serverId, loadingServers, servers, selectedServer, navigate]);

  useEffect(() => {
    if (categorySlug && !loadingCategories && categories.length > 0 && !selectedCategory) {
      navigate(serverId ? `/loja/${serverId}` : '/loja', { replace: true });
    }
  }, [categorySlug, loadingCategories, categories, selectedCategory, navigate, serverId]);

  const filteredProducts = selectedCategory
    ? allProducts.filter((p) => p.categoryId === selectedCategory.id)
    : allProducts;

  const formatPrice = (price: number) =>
    new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(price);

  const formatCredits = (credits: number) =>
    new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(credits);

  // Breadcrumbs
  const breadcrumbs = [
    { label: t('store.shop.page_title'), href: '/loja' },
    ...(selectedServer ? [{ label: selectedServer.name, href: serverId && !categorySlug ? undefined : `/loja/${serverId}` }] : []),
    ...(selectedCategory ? [{ label: selectedCategory.name }] : []),
  ];

  const isLoading = loadingServers || (serverId && loadingCategories) || (categorySlug && loadingProducts);

  return (
    <div>
      <PageHero title={t('store.shop.page_title')} breadcrumbs={breadcrumbs} />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 -mt-24 pt-24 relative z-10">
        {/* User Welcome Card */}
        {isAuthenticated && user && (
          <Card className="floating-card bg-card/50 backdrop-blur-sm border border-border/50 mb-8">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="relative status-online">
                    <Avatar className="h-14 w-14">
                      <AvatarImage src={getAvatarUrl(user?.username, 128)} />
                      <AvatarFallback className="bg-primary/20 text-primary text-xl">
                        {user?.username?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div>
                    <p className="text-xs text-primary uppercase tracking-wider">
                      {t('store.shop.welcome_back')}
                    </p>
                    <p className="font-display font-bold text-xl text-foreground">
                      {user?.username}
                    </p>
                    <Badge variant="secondary" className="mt-1">
                      {user?.role || t('store.shop.default_role')}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center md:text-right">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      {t('store.shop.available_balance')}
                    </p>
                    <p className="font-display font-bold text-2xl text-accent">
                      {formatCredits(user?.credits || 0)}
                    </p>
                  </div>
                  <Button
                    className="bg-primary hover:bg-primary-hover text-primary-foreground"
                    onClick={() => setCreditsDialogOpen(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('store.shop.add_credits')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/*
          * Lateral com largura fixa em vez de 1/4 da tela: em 1024 px ela
          * ficava com 220 px e os textos passavam da borda.
          */}
        <div className="grid grid-cols-1 lg:grid-cols-[16rem_minmax(0,1fr)] xl:grid-cols-[18rem_minmax(0,1fr)] gap-6 lg:gap-8">
          {/* Sidebar */}
          <aside className="min-w-0">
            <div className="sticky top-24 max-h-[calc(100vh-6rem)] overflow-y-auto flex flex-col gap-6 pr-1">
              <div className="floating-card bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl">
                <div className="p-6">
                  <h3 className="text-sm text-muted-foreground uppercase tracking-wider mb-4">
                    {t('store.shop.navigation')}
                  </h3>
                  <div className="space-y-1">
                    {/* All servers link (a loja de produtos digitais não tem servidor: vai direto às categorias) */}
                    {!lojaDigital && (
                    <Link to="/loja">
                      <Button
                        variant={!serverId ? 'secondary' : 'ghost'}
                        className="w-full justify-start"
                      >
                        <Grid3X3 className="w-4 h-4 mr-2" />
                        {t('store.shop.all_servers')}
                      </Button>
                    </Link>
                    )}

                    {/* Server list in sidebar when a server is selected */}
                    {serverId && !lojaDigital && servers.map((sv) => (
                      <Link key={sv.id} to={`/loja/${sv.id}`}>
                        <Button
                          variant={sv.id === serverId ? 'secondary' : 'ghost'}
                          className="w-full justify-start text-left"
                        >
                          {sv.imageUrl ? (
                            <img src={sv.imageUrl} alt={sv.name} className="w-4 h-4 mr-2 rounded object-cover" />
                          ) : (
                            <Server className="w-4 h-4 mr-2" />
                          )}
                          <span className="truncate">{sv.name}</span>
                        </Button>
                      </Link>
                    ))}

                    {/* Category list in sidebar when server is selected */}
                    {serverId && categories.length > 0 && (
                      <>
                        <div className="pt-2 pb-1">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider px-2">
                            {t('store.shop.categories_heading')}
                          </p>
                        </div>
                        <Link to={`/loja/${serverId}`}>
                          <Button
                            variant={!categorySlug ? 'secondary' : 'ghost'}
                            className="w-full justify-start"
                          >
                            <Grid3X3 className="w-4 h-4 mr-2" />
                            {t('store.shop.all_categories')}
                          </Button>
                        </Link>
                        {categories.map((cat) => (
                          <Link key={cat.id} to={`/loja/${serverId}/${cat.slug}`}>
                            <Button
                              variant={cat.slug === categorySlug ? 'secondary' : 'ghost'}
                              className="w-full justify-start text-left"
                            >
                              <ChevronRight className="w-4 h-4 mr-2 shrink-0" />
                              <span className="truncate">{cat.name}</span>
                            </Button>
                          </Link>
                        ))}
                      </>
                    )}

                    <Link to="/perfil?tab=orders">
                      <Button variant="ghost" className="w-full justify-start">
                        <Clock className="w-4 h-4 mr-2" />
                        {t('store.shop.purchase_history')}
                      </Button>
                    </Link>
                  </div>

                  {/* Help Card */}
                  <div className="mt-6 rounded-lg border border-border/30 bg-background/30 backdrop-blur-sm p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <HelpCircle className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{t('store.shop.need_help')}</p>
                        <Link
                          to="/suporte"
                          className="text-sm text-primary hover:text-primary-hover flex items-center gap-1"
                        >
                          {t('store.shop.contact_support')}
                          <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <CartPreview />
            </div>
          </aside>

          {/* Main Content */}
          <main className="min-w-0">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="w-1 h-6 bg-primary rounded-full" />
                <h2 className="font-display font-bold text-xl text-foreground">
                  {selectedCategory
                    ? selectedCategory.name
                    : selectedServer
                    ? selectedServer.name
                    : t('store.shop.select_server')}
                </h2>
              </div>

              {selectedCategory && (
                <div className="flex items-center gap-2">
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : !serverId ? (
              /* ── STEP 1: Selecionar Servidor ── */
              <>
                {servers.length === 0 ? (
                  <Card className="bg-card/50 backdrop-blur-sm border border-border/50">
                    <CardContent className="p-12 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                          <Server className="w-8 h-8 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-display font-bold text-xl text-foreground mb-2">
                            {t('store.shop.no_servers_title')}
                          </h3>
                          <p className="text-muted-foreground">
                            {t('store.shop.no_servers_desc')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {servers.map((sv) => (
                      <Link key={sv.id} to={`/loja/${sv.id}`}>
                        <Card className="group overflow-hidden card-interactive bg-card border-border h-full cursor-pointer hover:border-primary/50 transition-colors">
                          <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                              {sv.imageUrl ? (
                                <img
                                  src={sv.imageUrl}
                                  alt={sv.name}
                                  className="w-16 h-16 rounded-xl object-cover border border-border group-hover:border-primary/50 transition-colors"
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center border border-border group-hover:border-primary/50 transition-colors">
                                  <Server className="w-8 h-8 text-primary" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h3 className="font-display font-bold text-lg text-foreground group-hover:text-primary transition-colors truncate">
                                  {sv.name}
                                </h3>
                                <Badge variant="outline" className="mt-1 capitalize text-xs">
                                  {sv.gameType}
                                </Badge>
                                {sv.status === 'maintenance' && (
                                  <Badge className="ml-2 mt-1 bg-yellow-500 text-xs">
                                    {t('store.shop.maintenance')}
                                  </Badge>
                                )}
                              </div>
                              <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            ) : !categorySlug ? (
              /* ── STEP 2: Selecionar Categoria ── */
              <>
                {categories.length === 0 ? (
                  <Card className="bg-card/50 backdrop-blur-sm border border-border/50">
                    <CardContent className="p-12 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                          <ShoppingBag className="w-8 h-8 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-display font-bold text-xl text-foreground mb-2">
                            {t('store.shop.no_categories_title')}
                          </h3>
                          <p className="text-muted-foreground">
                            {t('store.shop.no_categories_desc')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {categories.map((category) => (
                      <CategoryCard key={category.id} category={category} serverId={serverId} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* ── STEP 3: Produtos ── */
              <>
                {loadingProducts ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <Card className="bg-card/50 backdrop-blur-sm border border-border/50">
                    <CardContent className="p-12 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                          <Package className="w-8 h-8 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-display font-bold text-xl text-foreground mb-2">
                            {t('store.shop.no_products_title')}
                          </h3>
                          <p className="text-muted-foreground">
                            {t('store.shop.no_products_desc')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div
                    className={`grid gap-6 ${
                      viewMode === 'grid'
                        ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'
                        : 'grid-cols-1'
                    }`}
                  >
                    {comAnuncioNoCatalogo(filteredProducts.map((product) => (
                      <ProductCard key={product.id} product={product} serverId={serverId} />
                    )))}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      <AddCreditsDialog open={creditsDialogOpen} onOpenChange={setCreditsDialogOpen} />
    </div>
  );
}
